import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatNumber, isoDate } from '../lib/format';
import type { RemoteWorkDetail, RemoteWorkPreview } from '../lib/remote-work';
import { FormGrid, FormPage } from '../components/FormPage';
import { Detail, Field, Input, Textarea, useToast } from '../components/ui';
import { remoteWorkDates } from './RemoteWork';

/**
 * Route: `/remote-work/new` — ask to work remotely for one day or more, seeing
 * first how many working days it takes and who approves it: the team leader,
 * then an administrator. Those who work remotely already are refused by the API.
 */
export function RemoteWorkFormPage() {
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const today = isoDate();
  const [form, setForm] = useState({ startDate: today, endDate: today, reason: '' });

  const rangeOk = !!form.startDate && !!form.endDate && form.endDate >= form.startDate;
  const preview = useQuery({
    queryKey: ['remote-work', 'preview', form.startDate, form.endDate],
    queryFn: () => api.get<RemoteWorkPreview>('/remote-work/preview', { startDate: form.startDate, endDate: form.endDate }),
    enabled: rangeOk,
    retry: false,
  });

  const save = useMutation({
    mutationFn: () =>
      api.post('/remote-work', { startDate: form.startDate, endDate: form.endDate, reason: form.reason.trim() }),
    onSuccess: () => {
      toast.success(t('remoteWork.requested'));
      void queryClient.invalidateQueries({ queryKey: ['remote-work'] });
      navigate('/remote-work?tab=mine');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const days = preview.data?.days ?? null;
  const overlap = preview.data?.overlapping ?? null;
  const leaders = preview.data?.teamLeaders ?? [];
  const names = leaders.map((p) => localized(p)).join(locale === 'ar' ? '، ' : ', ');

  return (
    <FormPage
      title={t('remoteWork.new')}
      subtitle={t('remoteWork.subtitle')}
      backTo="/remote-work?tab=mine"
      backLabel={t('remoteWork.title')}
      submitLabel={t('remoteWork.send')}
      submitting={save.isPending}
      disabled={!rangeOk || !form.reason.trim() || !preview.data || days === 0 || !!overlap}
      onSubmit={() => save.mutate()}
      summary={
        rangeOk && (
          <dl>
            <Detail label={t('remoteWork.days')}>
              <span className="tabular">
                {preview.isLoading ? '…' : preview.error ? '—' : t('remoteWork.workingDays', { n: formatNumber(days, locale) })}
              </span>
            </Detail>
            {preview.data && (
              <Detail label={t('remoteWork.whoApproves')}>
                {leaders.length > 0 ? (
                  <ol className="inline-flex flex-col items-end gap-0.5">
                    <li>
                      <span className="tabular">{formatNumber(1, locale)}.</span> {t('remoteWork.whoLeader', { names })}
                    </li>
                    <li>
                      <span className="tabular">{formatNumber(2, locale)}.</span> {t('remoteWork.whoAdmin')}
                    </li>
                  </ol>
                ) : (
                  t('remoteWork.whoNoLeader')
                )}
              </Detail>
            )}
            {overlap && (
              <p className="pt-1 text-body-sm text-warning-fg dark:text-warning-dark">
                {t('remoteWork.overlaps', {
                  dates: remoteWorkDates(overlap, locale),
                  status: t(`remoteWork.status.${overlap.status}` as TranslationKey),
                })}
              </p>
            )}
            {days === 0 && <p className="pt-1 text-body-sm text-warning-fg dark:text-warning-dark">{t('remoteWork.noWorkingDays')}</p>}
            {preview.error && <p className="pt-1 text-body-sm text-error-fg dark:text-error-dark">{(preview.error as Error).message}</p>}
          </dl>
        )
      }
    >
      <FormGrid>
        <Field label={t('remoteWork.from')} required>
          <Input
            type="date"
            min={today}
            value={form.startDate}
            autoFocus
            onChange={(e) =>
              setForm({
                ...form,
                startDate: e.target.value,
                // Moving the first day past the last drags the last along — one day stays one day.
                endDate: form.endDate < e.target.value ? e.target.value : form.endDate,
              })
            }
          />
        </Field>
        <Field label={t('remoteWork.to')} required hint={t('remoteWork.daysHint')}>
          <Input type="date" min={form.startDate} value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
        </Field>
        <Field label={t('common.reason')} required hint={t('remoteWork.reasonHint')} className="sm:col-span-2">
          <Textarea value={form.reason} maxLength={1000} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
        </Field>
      </FormGrid>
    </FormPage>
  );
}

/**
 * Route: `/remote-work/:id/reject` — by its team leader or, after them, an
 * administrator. The employee reads the reason, so there must be one.
 */
export function RejectRemoteWorkFormPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');

  const request = useQuery({
    queryKey: ['remote-work', 'one', id],
    queryFn: () => api.get<RemoteWorkDetail>(`/remote-work/${id}`),
  });

  const reject = useMutation({
    mutationFn: () => api.post(`/remote-work/${id}/reject`, { note: note.trim() }),
    onSuccess: () => {
      toast.success(t('remoteWork.rejectedToast'));
      void queryClient.invalidateQueries({ queryKey: ['remote-work'] });
      // Back to the queue or the request's page, whichever it came from.
      if (location.key !== 'default') navigate(-1);
      else navigate(`/remote-work/${id}`, { replace: true });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const r = request.data;

  return (
    <FormPage
      title={t('remoteWork.rejectTitle')}
      subtitle={r?.can.decide ? t(r.waitingFor === 'LEADER' ? 'remoteWork.rejectAsLeader' : 'remoteWork.rejectAsAdmin') : undefined}
      backLabel={t('common.back')}
      submitLabel={t('common.reject')}
      destructive
      submitting={reject.isPending}
      disabled={!note.trim() || (r && !r.can.decide)}
      loading={request.isLoading}
      error={request.error}
      onRetry={() => void request.refetch()}
      onSubmit={() => reject.mutate()}
      summary={
        r && (
          <dl>
            <Detail label={t('common.employee')}>{localized(r.user)}</Detail>
            <Detail label={t('remoteWork.dates')}>
              <span className="tabular">{remoteWorkDates(r, locale)}</span>
            </Detail>
            <Detail label={t('remoteWork.days')}>{formatNumber(r.days, locale)}</Detail>
            <Detail label={t('common.reason')}>{r.reason}</Detail>
          </dl>
        )
      }
    >
      <Field label={t('remoteWork.rejectReason')} required>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} autoFocus />
      </Field>
    </FormPage>
  );
}
